import { useState } from "react";
import { Stack } from "@/core/components/ui/stack";
import {
  Tabs,
  TabsTrigger,
  TabsContent,
  TabsListWithIndicator,
} from "@/core/components/ui/tabs";
import { toast } from "@/core/components/ui/toast";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { NoteList } from "@/business-settings/customers/common/note-list";
import { formatPhoneNumber } from "@/core/utils/phone";
import {
  useGetCustomerNotes,
  useGetCustomer,
  useOptOutCustomer,
} from "@/customer/query";
import { LeadDetailsForm } from "./lead-details-form";
import { LeadSidebarSkeleton } from "./lead-sidebar-skeleton";

interface LeadSidebarProps {
  businessId: string;
  leadId: string | undefined;
}

const LeadSidebar = ({ businessId, leadId }: LeadSidebarProps) => {
  const { data: lead, isLoading } = useGetCustomer(leadId);

  const [activeTab, setActiveTab] = useState("details");
  const { resetSidebar } = useBusinessSettingsSidebar();
  const optOutCustomer = useOptOutCustomer(businessId);

  const { data: notes = [] } = useGetCustomerNotes(leadId || "");

  const headerText = "Leads";
  const name =
    lead?.name || formatPhoneNumber(lead?.phone_number) || "New Lead";

  const handleOptOut = () => {
    if (leadId) {
      optOutCustomer.mutate(leadId, {
        onSuccess: () => {
          toast.success("Customer opted out from messaging");
          resetSidebar();
        },
        onError: () => {
          toast.error("Failed to opt out customer from messaging");
        },
      });
    }
  };

  const dropdownActions = leadId
    ? [
        {
          label: "Opt out from messaging",
          onClick: handleOptOut,
          variant: "destructive" as const,
        },
      ]
    : undefined;

  if (isLoading) {
    return <LeadSidebarSkeleton />;
  }

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={headerText}
        name={name}
        onClose={resetSidebar}
        dropdownActions={dropdownActions}
      />
      <div className="flex-1 overflow-hidden">
        <Stack gap="none" fullWidth className="h-full">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="flex w-full flex-1 flex-col overflow-hidden px-6"
          >
            <TabsListWithIndicator
              containerClassName="w-full"
              className="w-full justify-center"
            >
              <TabsTrigger value="details" className="flex-1">
                Details
              </TabsTrigger>
              <TabsTrigger value="notes" className="flex-1">
                Notes ({notes.length})
              </TabsTrigger>
            </TabsListWithIndicator>

            <TabsContent value="details" className="pt-4">
              <LeadDetailsForm businessId={businessId} initialData={lead} />
            </TabsContent>

            <TabsContent value="notes">
              <NoteList notes={notes} customerId={leadId} />
            </TabsContent>
          </Tabs>
        </Stack>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};

export default LeadSidebar;
